import { useState, useEffect } from "react";
import { UserLayout } from "./components/layout/UserLayout";
import { Pagination } from "../../Components/Pagination";
import Container from "./components/Container";
import Text from "./components/Text";
import Effects from "./components/Effects";
import BlogCard from "./components/cards/BlogCard";
import PageHero from "./components/PageHero";
import { getImg } from "../../Components/Utils";
import Img from "./components/Img";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import { useFetchBlogPageQuery } from "../../stores/api/pages.api";

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const BlogSkeleton = () => (
  <UserLayout>
    <Container variant="xnarrow" className="px-4 lg:px-8 py-12">
      <div className="flex flex-col lg:flex-row gap-10">
        <aside className="w-full lg:w-[280px] flex-shrink-0 space-y-10">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-60 w-full" />
          <Skeleton className="h-32 w-full" />
        </aside>
        <main className="flex-1 min-w-0">
          <Skeleton className="aspect-[16/9] w-full mb-4" />
          <Skeleton className="h-6 w-3/4 mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <Skeleton className="aspect-[3/1.2]  w-full mb-3" />
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        </main>
      </div>
    </Container>
  </UserLayout>
);

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState(null); // category slug
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(9);
  const [pageJump, setPageJump] = useState("1");

  // Server-side pagination + category filter.
  const { data, isLoading } = useFetchBlogPageQuery({
    page,
    limit: perPage,
    ...(activeCategory ? { category: activeCategory } : {}),
  });

  useEffect(() => {
    setPageJump(String(page));
  }, [page]);

  const changeCategory = (slug) => {
    setActiveCategory((prev) => (prev === slug ? null : slug));
    setPage(1);
  };

  if (isLoading) return <BlogSkeleton />;

  const {
    categories = [],
    recentPosts = [],
    tags = [],
    instagram = [],
    posts = [],
    pagination = {},
  } = data ?? {};

  // Featured post only on the first page; the rest fill the grid.
  const featured = page === 1 ? posts[0] : null;
  const gridPosts = page === 1 ? posts.slice(1) : posts;

  const totalPages = Math.max(pagination.total_pages ?? 1, 1);
  const jumpToPage = () => {
    const n = Number(pageJump);
    if (!Number.isNaN(n)) setPage(Math.min(Math.max(n, 1), totalPages));
  };

  return (
    <UserLayout>
      <PageHero
        title="All Blogs"
        crumbs={[
          { label: "Home", href: ROUTE_PATHS.HOME },
          { label: "Blog" },
        ]}
      />

      <Container variant="narrow" className="px-4 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* ── Aside ── */}
          <aside className="w-full lg:w-[280px] flex-shrink-0 space-y-10 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            {/* Blog categories */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider text-xs"
              >
                Blog categories
              </Text>
              <ul className="space-y-2.5">
                {categories.map((cat) => (
                  <li key={cat.slug}>
                    <button
                      onClick={() => changeCategory(cat.slug)}
                      className={`text-sm transition-colors hover:text-blue-600 flex items-center justify-between w-full ${activeCategory === cat.slug ? "text-blue-600 font-medium" : "text-gray-500"}`}
                    >
                      <span>{cat.label}</span>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{cat.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Posts */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider text-xs"
              >
                Recent Post
              </Text>
              <ul className="space-y-4">
                {recentPosts.map((post) => (
                  <li key={post.slug}>
                    <Text
                      is="a"
                      href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", post.slug)}
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-50 border border-gray-100">
                        <Img
                          src={getImg(post.img)}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Text
                          is="span"
                          className="text-[9px] font-bold text-blue-600 uppercase tracking-wider block mb-0.5"
                        >
                          {post.category}
                        </Text>
                        <Text
                          is="p"
                          className="text-xs font-medium text-gray-800 group-hover:text-blue-600 transition-colors leading-snug line-clamp-2"
                        >
                          {post.title}
                        </Text>
                      </div>
                    </Text>
                  </li>
                ))}
              </ul>
            </div>

            {/* Blog tags */}
            <div>
              <Text
                is="h3"
                className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider text-xs"
              >
                Blog tags
              </Text>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Text
                    key={tag}
                    is="a"
                    href="#"
                    className="text-xs border border-gray-200 rounded-md px-2.5 py-1.5 text-gray-600 hover:border-gray-900 hover:text-gray-900 transition-colors bg-white"
                  >
                    {tag}
                  </Text>
                ))}
              </div>
            </div>
          </aside>

          {/* ── Main blog grid ── */}
          <main className="flex-1 min-w-0">
            {/* Featured post styled beautifully with description */}
            {featured && (
              <Effects type="scroll-fadeup">
                <div className="mb-12 bg-white border border-gray-100 rounded-2xl overflow-hidden p-4 sm:p-6 group hover:shadow-md transition-shadow duration-300">
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    {/* Balanced Image side */}
                    <div className="w-full md:w-1/2 relative overflow-hidden rounded-xl aspect-[16/10] md:max-h-[260px] flex-shrink-0">
                      <Img
                        src={getImg(featured.img)}
                        alt={featured.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                      <span className="absolute top-3 left-3 text-[10px] font-bold bg-white/90 backdrop-blur-xs text-blue-600 px-2.5 py-1 uppercase tracking-wider rounded-md shadow-xs">
                        {featured.category}
                      </span>
                    </div>

                    {/* Content side */}
                    <div className="w-full md:w-1/2 flex flex-col justify-center">
                      <Text
                        is="h2"
                        className="text-lg sm:text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-blue-600 transition-colors"
                      >
                        <Text
                          is="a"
                          href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", featured.slug)}
                        >
                          {featured.title}
                        </Text>
                      </Text>
                      
                      {/* Short Description added here */}
                      <Text
                        is="p"
                        className="text-sm text-gray-500 mb-4 line-clamp-3 leading-relaxed"
                      >
                        {featured.description || "Explore this latest update to learn more about application configurations, operational advancements, and implementation details."}
                      </Text>

                      <div>
                        <Text
                          is="a"
                          href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", featured.slug)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          Read full article <span>→</span>
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              </Effects>
            )}

            {/* Post grid */}
            <Effects type="scroll-fadeup">
              <Container
                variant="full"
                showEmptyState={!data || !posts.length}
                emptyMessage="No blog posts found"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gridPosts.map((post, i) => (
                    <BlogCard
                      key={i}
                      img={post.img}
                      hoverImg={post.hoverImg}
                      title={post.title}
                      description={post.description}
                      href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", post.slug)}
                      category={post.category?.name ?? post.category ?? null}
                    />
                  ))}
                </div>
              </Container>

              {posts.length > 0 && totalPages > 1 && (
                <Pagination
                  variant="blue"
                  className="mt-8"
                  currentPage={page}
                  setCurrentPage={setPage}
                  totalPages={totalPages}
                  rowsPerPage={perPage}
                  setRowsPerPage={setPerPage}
                  pageJump={pageJump}
                  setPageJump={setPageJump}
                  jumpToPage={jumpToPage}
                />
              )}
            </Effects>
          </main>
        </div>
      </Container>
    </UserLayout>
  );
};

export default Blog;